import React from 'react';

/**
 * FilesExplorerHeader Component
 * Breadcrumbs, folder title, search trigger button, Active/Archived switcher, and Grid/List toggle
 */
export default function FilesExplorerHeader({
    projectName = 'Shopify Store Redesign',
    folderName = 'Shared Documents',
    fileCount = null,
    viewMode = 'grid',
    onViewModeChange,
    onOpenSearch,
    onUploadClick,
    onRefresh,
    isRefreshing = false,
    fileStatus = 'active',
    onFileStatusChange,
    isArchivedProject = false,
}) {
    return (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
                {/* Breadcrumbs */}
                <nav className="flex items-center text-xs font-medium text-[#727786] dark:text-[#9aa5bc] mb-1">
                    <span className="hover:text-[#0070F3] cursor-pointer transition-colors">
                        {projectName}
                    </span>
                    <span className="material-symbols-outlined text-sm mx-1">
                        chevron_right
                    </span>
                    <span className="text-[#0b1c30] dark:text-[#eaf1ff] font-semibold">
                        {folderName}
                    </span>
                    {isArchivedProject && (
                        <span className="ml-2 px-2 py-0.2 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                            Archived Project
                        </span>
                    )}
                </nav>
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold tracking-tight text-[#0b1c30] dark:text-[#eaf1ff]">
                        {folderName}
                    </h2>
                    {typeof fileCount === 'number' && (
                        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border shadow-2xs ${
                            fileStatus === 'archived' || isArchivedProject
                                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                                : 'bg-[#0070F3]/10 text-[#0070F3] dark:bg-[#0070F3]/25 dark:text-[#60a5fa] border-[#0070F3]/20'
                        }`}>
                            {fileCount} {fileCount === 1 ? 'file' : 'files'}
                        </span>
                    )}
                </div>
            </div>

            {/* Actions: Upload, Active/Archived toggle, Search modal trigger, and Grid/List toggle */}
            <div className="flex items-center gap-3 flex-wrap">
                {/* Upload Button (hidden if in archived project) */}
                {!isArchivedProject && (
                    <button
                        type="button"
                        onClick={onUploadClick}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#0070F3] hover:bg-[#0059c5] text-white text-xs font-semibold shadow-sm transition-all active:scale-95"
                    >
                        <span className="material-symbols-outlined text-base leading-none">
                            upload_file
                        </span>
                        Upload
                    </button>
                )}

                {/* Active vs Archived Files Switcher (for active projects) */}
                {!isArchivedProject && (
                    <div className="flex bg-[#eff4ff] dark:bg-[#1a2538] p-1 rounded-lg border border-[#c1c6d7] dark:border-[#344258]">
                        <button
                            type="button"
                            onClick={() => onFileStatusChange?.('active')}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                                fileStatus === 'active'
                                    ? 'bg-[#0070F3] text-white shadow-xs'
                                    : 'text-[#555d73] dark:text-[#9aa5bc] hover:text-[#0b1c30] dark:hover:text-white'
                            }`}
                        >
                            <span className="material-symbols-outlined text-sm">folder</span>
                            Active
                        </button>
                        <button
                            type="button"
                            onClick={() => onFileStatusChange?.('archived')}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                                fileStatus === 'archived'
                                    ? 'bg-[#ba1a1a] text-white shadow-xs'
                                    : 'text-[#555d73] dark:text-[#9aa5bc] hover:text-[#ba1a1a] dark:hover:text-[#ffdad6]'
                            }`}
                        >
                            <span className="material-symbols-outlined text-sm">archive</span>
                            Archived
                        </button>
                    </div>
                )}

                {/* Search trigger button (Icon-only) */}
                <button
                    type="button"
                    onClick={onOpenSearch}
                    className="p-2 text-[#414754] dark:text-[#9aa5bc] hover:text-[#0070F3] dark:hover:text-white hover:bg-[#eff4ff] dark:hover:bg-[#212f45] rounded-lg transition-colors border border-[#c1c6d7]/60 dark:border-[#344258] shadow-2xs"
                    title="Search files, projects, or clients (Cmd/Ctrl + K)"
                    aria-label="Search files (Cmd/Ctrl + K)"
                >
                    <span className="material-symbols-outlined text-lg leading-none">
                        search
                    </span>
                </button>

                {/* Grid / List switcher */}
                <div className="flex bg-[#eff4ff] dark:bg-[#1a2538] p-1 rounded-lg border border-[#c1c6d7] dark:border-[#344258]">
                    <button
                        type="button"
                        onClick={() => onViewModeChange?.('grid')}
                        className={`
                            flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-all
                            ${viewMode === 'grid'
                                ? 'bg-[#0070F3] text-white shadow-sm'
                                : 'text-[#414754] dark:text-[#9aa5bc] hover:bg-white/50 dark:hover:bg-[#212f45]'
                            }
                        `}
                    >
                        <span className="material-symbols-outlined text-base">grid_view</span>
                        Grid
                    </button>
                    <button
                        type="button"
                        onClick={() => onViewModeChange?.('list')}
                        className={`
                            flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-all
                            ${viewMode === 'list'
                                ? 'bg-[#0070F3] text-white shadow-sm'
                                : 'text-[#414754] dark:text-[#9aa5bc] hover:bg-white/50 dark:hover:bg-[#212f45]'
                            }
                        `}
                    >
                        <span className="material-symbols-outlined text-base">list</span>
                        List
                    </button>
                </div>

                {/* Refresh trigger button — subtle, low-profile ghost icon placed quietly at the end */}
                <button
                    type="button"
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    className="p-1.5 text-[#727786]/50 dark:text-[#9aa5bc]/40 hover:text-[#414754] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded-md transition-all opacity-60 hover:opacity-100"
                    title="Refresh files"
                    aria-label="Refresh files"
                >
                    <span className={`material-symbols-outlined text-base leading-none ${isRefreshing ? 'animate-spin text-[#0070F3]' : ''}`}>
                        refresh
                    </span>
                </button>
            </div>
        </div>
    );
}
